/**
 * The location contract — requirements 3.1 and 3.4, and the one payload in the system with a
 * live defect behind it.
 *
 * Today the public payload is assembled by a hand-written projection in a route handler
 * (`APR-Dashboard/server/routes.ts`, `/api/locations/active/export`) that renames columns on the
 * way out: `pueblo` → `ciudad`, `coordenadas_lat` → `latitude`, `coordenadas_lng` → `longitude`.
 * The consumer declares the same six fields independently in
 * `ApuestaPR-Web/client/src/types/location.ts`. Nothing connects the two, so renaming `pueblo`
 * blanks the public map with no error anywhere. This file is the shape both sides now read, and
 * `apps/api/src/mappers/locations.ts` is the declared link to the table.
 *
 * ## The public field set, and why it is these seven
 *
 * Six come from the live endpoint, because they are what the rebuilt site renders: `nombre` is
 * the card title and the marker's accessible name, `ciudad` is what the town filter keys on,
 * `direccion` is the second line and the destination of the "directions" link, `telefono` is the
 * popup's call link, and `latitude` / `longitude` place the marker.
 *
 * The seventh is `id`, which the live endpoint does not send. The site fabricates one instead —
 * `` `${ciudad}-${index}` `` in `transformApiLocation` — and uses it as a React key, as the
 * `data-testid` suffix on every marker, and as the popup's identity. That is derived from data
 * plus list position, and three of the twelve seeded locations are in Carolina and three in San
 * Juan, so identity moves whenever the order does: inserting a row silently reassigns the keys
 * of everything after it, and no location has a stable handle a link or a selection could hold.
 * The table has a serial primary key; sending it costs one integer and removes the whole class
 * of problem.
 *
 * Nothing else from the row is public. `locations` also holds static IP addresses, gaming
 * licence numbers, and named contacts with their phone numbers — none of which belongs on an
 * unauthenticated wire, and none of which the site has ever displayed.
 */
import { z } from 'zod';
import { nonEmptyUpdate } from './content/common.js';
/**
 * Nullable rather than required, which is the second half of the defect above: the columns
 * behind `telefono`, `latitude`, and `longitude` are all nullable in the database, and the
 * consumer's hand-written type declares them `string` and `number`. So today a location with no
 * coordinates is typed as having them, and a map that trusts the type puts a marker at 0°, 0° in
 * the Gulf of Guinea. Declaring the absence here forces the site to decide what to do about it.
 *
 * The three text fields are the opposite case: `notNull` in the database, and load-bearing —
 * a card with no title, no town, and no address is not a location. `.min(1)` means a row that
 * somehow holds a blank one fails its own response validation rather than rendering an empty
 * card. What the API does with that failure is Task 8's decision; the contract's job is to make
 * it a decision rather than a silent blank.
 */
export const publicLocationSchema = z.object({
    id: z.number().int().positive(),
    nombre: z.string().min(1),
    /** `locations.pueblo`. The rename is declared in the mapper, not in a route handler. */
    ciudad: z.string().min(1),
    direccion: z.string().min(1),
    telefono: z.string().min(1).nullable(),
    /** `locations.coordenadas_lat`, stored as `real` — float precision, not decimal. */
    latitude: z.number().min(-90).max(90).nullable(),
    /** `locations.coordenadas_lng`. */
    longitude: z.number().min(-180).max(180).nullable(),
});
/** The response body of the public locations endpoint. An array, as the live endpoint returns. */
export const publicLocationListSchema = z.array(publicLocationSchema);
/**
 * The admin payload: the public seven widened by the two fields the dashboard's location screen
 * reads and the public site has no business seeing — `estado`, which drives the status badge and
 * is also what the public query filters on, and `notas`, which is internal.
 *
 * Widened deliberately narrowly. The same table carries `ip_address`, `licencia_juego`,
 * `contacto_nombre`, `contacto_telefono`, and eight operational booleans, and the screen that
 * needs those is the location *detail* view, which belongs to a later spec. A payload that
 * shipped every column now would be a payload nobody has read in full.
 *
 * `estado` is a plain string, not an enum. The column is unconstrained `text` in the database and
 * the values in use disagree already: the dashboard's badge handles `activo`, `no_activo`, and
 * `en_proceso`, while the seeded data also contains `pendiente`. An enum here would reject rows
 * that exist. Constraining it is a schema change, and it belongs to whoever wants it.
 */
export const adminLocationSchema = publicLocationSchema.extend({
    estado: z.string().min(1),
    notas: z.string().nullable(),
});
export const adminLocationListSchema = z.array(adminLocationSchema);
/**
 * The public payload's field names, taken from the schema rather than written out again.
 *
 * Requirement 3.4 is that these names come from the contract and not from an ad-hoc alias in a
 * route handler. Deriving the list from `.shape` is what makes that checkable: the test asserting
 * the mapper's output keys and this list cannot both drift, because there is only one source.
 */
export const publicLocationFields = Object.freeze(Object.keys(publicLocationSchema.shape));
export const adminLocationFields = Object.freeze(Object.keys(adminLocationSchema.shape));
/**
 * The full location record — every operational field the table carries, for the location detail
 * screen a later spec deferred. `adminLocationSchema` above stays exactly as narrow as it is
 * today, because `GET /api/admin/locations` is open to every staff role including `agente` and
 * its test pins the exact field set; this wider shape is for the `admin`/`manager`-only detail
 * route this spec adds.
 *
 * Two renames, declared here rather than left to a mapper comment to discover:
 *
 * - `locations.otc_users` is a boolean flag ("this location has an OTC terminal enabled"), not
 *   the unrelated `otc_users` *table* of terminal login credentials this same spec introduces.
 *   Naming the field `otcUsersEnabled` on the wire keeps the two from reading as the same thing
 *   in a payload that, after this spec, sits beside literal `otcUsers` rows.
 * - Every boolean column on this table is nullable in the database despite carrying a `false`
 *   default — Drizzle only omits `null` from a column's type when a column is `notNull()`, and
 *   none of these are. The wire contract requires a definite `true`/`false`; the mapper is where
 * a stored `null` becomes `false`, so a form always has something to check or uncheck.
 */
export const locationDetailShape = {
    ...adminLocationSchema.shape,
    ipAddress: z.string().min(1).nullable(),
    contactoNombre: z.string().min(1).nullable(),
    contactoTelefono: z.string().min(1).nullable(),
    licenciaJuego: z.string().min(1).nullable(),
    hasStaticIp: z.boolean(),
    isUpdated: z.boolean(),
    yubikeyAssigned: z.boolean(),
    ipWhitelisted: z.boolean(),
    training: z.boolean(),
    gcApproval: z.boolean(),
    otcUsersEnabled: z.boolean(),
    createLocationInKambi: z.boolean(),
    tags: z.array(z.string().min(1)),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
};
export const locationDetailSchema = z.object(locationDetailShape);
/**
 * What an editor may create or replace. `id`, `createdAt`, and `updatedAt` are server-assigned;
 * everything else in {@link locationDetailShape} is editable, including `estado`, which stays
 * free text for the reason `adminLocationSchema`'s own comment gives — the column is
 * unconstrained and the seeded data already disagrees with the dashboard badge's closed set.
 */
const locationEditableShape = (() => {
    const { id: _id, createdAt: _createdAt, updatedAt: _updatedAt, ...editable } = locationDetailShape;
    return editable;
})();
export const createLocationSchema = z.object(locationEditableShape);
export const updateLocationSchema = nonEmptyUpdate(z.object(locationEditableShape));
//# sourceMappingURL=locations.js.map